import { useCallback, useEffect, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { masterUsersApi } from '@/api/masterUsers.api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { dataTablePageProps, usePagination } from '@/hooks/usePagination';
import type { PlatformUser } from '@/types/platformUser';
import { formatDateTime } from '@/utils/format';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';

const schema = z.object({
  email: z.string().email('Enter a valid email'),
  password: z.string().min(8, 'At least 8 characters'),
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
});

type FormData = z.infer<typeof schema>;

export function MasterAdminsPage() {
  const pagination = usePagination();
  const [data, setData] = useState<PlatformUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({ resolver: zodResolver(schema) });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await masterUsersApi.list({
        page: pagination.page,
        size: pagination.size,
        search: pagination.search || undefined,
        masterOnly: true,
        activeOnly: false,
      });
      setData(res.content);
      pagination.applyPageResponse(res);
    } catch (err) {
      showApiError(err, 'Failed to load master administrators');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.size, pagination.search, pagination]);

  useEffect(() => {
    void load();
  }, [load]);

  const columns: Column<PlatformUser>[] = [
    {
      key: 'name',
      header: 'Name',
      render: (r) => [r.firstName, r.lastName].filter(Boolean).join(' '),
    },
    { key: 'email', header: 'Email' },
    {
      key: 'mfaEnabled',
      header: 'MFA',
      render: (r) => (r.mfaEnabled === true ? 'On' : 'Off'),
    },
    {
      key: 'lastLogin',
      header: 'Last sign-in',
      render: (r) => (r.lastLogin ? formatDateTime(r.lastLogin) : 'Never'),
    },
    {
      key: 'active',
      header: 'Status',
      render: (r) => (
        <span className={r.active ? 'text-green-600' : 'text-red-600'}>{r.active ? 'Active' : 'Inactive'}</span>
      ),
    },
  ];

  const onSubmit = async (form: FormData) => {
    setSaving(true);
    try {
      await masterUsersApi.createMasterAdmin(form);
      notify.success('Master administrator created');
      setModalOpen(false);
      reset();
      void load();
    } catch (err) {
      showApiError(err, 'Failed to create master administrator');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page-content">
      <PageHeader
        title="Master administrators"
        description="Platform operators who can access the master console"
        breadcrumbs={[{ label: 'Master', to: '/master' }, { label: 'Master admins' }]}
        actions={
          <Button
            onClick={() => {
              reset();
              setModalOpen(true);
            }}
          >
            <AddIcon fontSize="small" />
            Add master admin
          </Button>
        }
      />
      <DataTable
        columns={columns}
        data={data}
        keyField="id"
        loading={loading}
        search={pagination.search}
        onSearchChange={(v) => {
          pagination.setSearch(v);
          pagination.resetPage();
        }}
        searchPlaceholder="Search master admins…"
        {...dataTablePageProps(pagination)}
      />
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title="New master administrator"
        footer={
          <>
            <Button variant="secondary" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit(onSubmit)} loading={saving}>
              Create
            </Button>
          </>
        }
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Input label="First name *" {...register('firstName')} error={errors.firstName?.message} />
          <Input label="Last name *" {...register('lastName')} error={errors.lastName?.message} />
          <Input
            label="Email *"
            type="email"
            className="sm:col-span-2"
            {...register('email')}
            error={errors.email?.message}
          />
          <Input
            label="Password *"
            type="password"
            className="sm:col-span-2"
            {...register('password')}
            error={errors.password?.message}
          />
        </div>
      </Modal>
    </div>
  );
}
